import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { View, Text, StyleSheet, Platform } from 'react-native';
// Render tooltip above all rows/containers on web via dynamic require (safe for native builds)
let createPortalWeb: ((node: any, container: any) => any) | null = null;
if (Platform.OS === 'web') {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const rd = require('react-dom');
    createPortalWeb = rd.createPortal;
  } catch (e) {
    createPortalWeb = null;
  }
}
import { useThemeColors } from '@/hooks/shared/useThemeColors';
import { useAnchoredPosition, AnchorRect } from '@/hooks/common/useAnchoredPosition';

interface DesktopOverflowTextProps {
  text: string;
  title?: string;
  numberOfLines?: number;
  tooltipText?: string; // optional full tooltip content
  align?: 'start' | 'center' | 'end';
  icon?: React.ReactNode; // optional SVG icon to render instead of text
}

export const DesktopOverflowText: React.FC<DesktopOverflowTextProps> = ({ text, title = 'Details', numberOfLines = 2, tooltipText, align = 'start', icon }) => {
  const { colors, semantic } = useThemeColors();
  const [hovered, setHovered] = useState(false);
  const [coords, setCoords] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const containerRef = useRef<View>(null);
  const tooltipRef = useRef<View>(null);
  const [measuredWidth, setMeasuredWidth] = useState<number | null>(null);

  // Measure on hover for precise portal positioning (web only)
  useLayoutEffect(() => {
    if (Platform.OS !== 'web') return;
    if (!hovered) return;
    const node: any = containerRef.current as any;
    if (node && node._node && typeof node._node.getBoundingClientRect === 'function') {
      const rect = node._node.getBoundingClientRect();
      setCoords({ x: rect.left, y: rect.top, w: rect.width, h: rect.height });
    } else if (node && typeof node.getBoundingClientRect === 'function') {
      const rect = node.getBoundingClientRect();
      setCoords({ x: rect.left, y: rect.top, w: rect.width, h: rect.height });
    }
  }, [hovered]);

  // Keep tooltip aligned on window resize while open
  useEffect(() => {
    if (Platform.OS !== 'web' || !hovered) return;
    const handler = () => {
      const node: any = containerRef.current as any;
      if (!node) return;
      const el = node._node || node;
      if (el && typeof el.getBoundingClientRect === 'function') {
        const rect = el.getBoundingClientRect();
        setCoords({ x: rect.left, y: rect.top, w: rect.width, h: rect.height });
      }
    };
    const g: any = globalThis as any;
    if (g && g.addEventListener) {
      g.addEventListener('resize', handler);
      g.addEventListener('scroll', handler, { passive: true });
      return () => {
        g.removeEventListener('resize', handler);
        g.removeEventListener('scroll', handler);
      };
    }
    return () => {};
  }, [hovered]);

  const contentText = (tooltipText ?? text) || '';
  const hasBody = !!contentText.trim();
  const anchorRect: AnchorRect | null = useMemo(() => {
    if (!coords) return null;
    return { x: coords.x, y: coords.y, w: coords.w, h: coords.h };
  }, [coords]);
  // Estimate panel width based on title/body length to improve horizontal clamping
  const textLen = useMemo(() => `${title ?? ''} ${contentText}`.trim().length, [title, contentText]);
  // Provide a hint width for positioning; the panel itself will size to content
  const hintWidth = useMemo(() => {
    if (Platform.OS !== 'web') return 240;
    const px = Math.round(textLen * 7.2) + 28;
    return Math.max(140, Math.min(420, px));
  }, [textLen]);
  // Center align by default; use measuredWidth when available for pixel-accurate centering
  const anchorWidthForCalc = measuredWidth ?? hintWidth;
  // Provide estimated height for more accurate flip-above positioning.
  // Title is ~18px line-height, body is ~18px each + paddings.
  const estimatedLines = Math.max(1, Math.ceil(((tooltipText ?? text)?.length || 0) / 38));
  const estimatedHeight = 6 /*mt*/ + 6 /*pb*/ + 6 /*pt*/ + 18 /*title*/ + (hasBody ? 18 * Math.min(6, estimatedLines) : 0);
  const anchored = useAnchoredPosition(anchorRect, { width: anchorWidthForCalc, maxHeight: 360, align, estimatedHeight });

  // Measure tooltip width after it is rendered in the portal (web only)
  useLayoutEffect(() => {
    if (Platform.OS !== 'web') return;
    if (!hovered) return;
    const g: any = globalThis as any;
    const measure = () => {
      const node: any = tooltipRef.current as any;
      const el = node?._node || node;
      if (el && typeof el.getBoundingClientRect === 'function') {
        const rect = el.getBoundingClientRect();
        if (rect && rect.width) setMeasuredWidth(rect.width);
      }
    };
    // Delay to next frame to ensure portal mounted
    const r = g?.requestAnimationFrame || ((cb: any) => setTimeout(cb, 16));
    r(() => measure());
  }, [hovered, title, contentText]);
  const tooltip = (
    <View style={[
      styles.tooltipContainer,
      { backgroundColor: colors.bg.modal, borderColor: colors.card.border },
      Platform.OS === 'web'
        ? ({ position: 'fixed', top: anchored.top, left: anchored.left, maxWidth: 420, visibility: measuredWidth ? 'visible' : 'hidden' } as any)
        : null,
    ]} ref={tooltipRef}>
      {!!title && <Text style={[styles.tooltipTitle, { color: semantic.text.primary }]}>{title}</Text>}
      {hasBody && <Text style={[styles.tooltipText, { color: semantic.text.secondary }]}>{contentText}</Text>}
    </View>
  );

  return (
    <View
      ref={containerRef}
      style={styles.container}
      {...(Platform.OS === 'web'
        ? {
            onMouseEnter: () => setHovered(true),
            onMouseLeave: () => setHovered(false),
          }
        : {})}
      pointerEvents={Platform.OS === 'web' ? 'auto' : undefined}
    >
      {icon ? (
        <View style={styles.iconContainer}>
          {icon}
        </View>
      ) : (
        <Text
          style={[styles.text, { color: semantic.text.primary }, Platform.OS === 'web' ? ({ whiteSpace: 'normal', wordBreak: 'break-word' } as any) : null]}
          numberOfLines={numberOfLines}
        >
          {text}
        </Text>
      )}
      {Platform.OS === 'web' && hovered && (!!title || !!(tooltipText ?? text))
        ? (() => {
            const g: any = globalThis as any;
            const target = g?.document?.body;
            return target && createPortalWeb ? createPortalWeb(tooltip, target) : null;
          })()
        : Platform.OS !== 'web' && hovered && !!(tooltipText ?? text)
        ? tooltip
        : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    height: '100%',
    position: 'relative',
  },
  text: {
    flex: 1,
    fontSize: 12,
  },
  tooltipContainer: {
    position: 'absolute',
    top: '100%',
    left: 0,
    marginTop: 6,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 8,
    paddingVertical: 6,
    zIndex: 10000,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
  },
  tooltipTitle: {
    fontSize: 11,
    fontWeight: '500',
    marginBottom: 2,
  },
  tooltipText: {
    fontSize: 11,
    lineHeight: 18,
    ...(Platform.OS === 'web' ? ({ whiteSpace: 'normal', wordBreak: 'normal' } as any) : null),
  },
  iconContainer: {
    alignItems: 'center',
    justifyContent: 'center',
  },
});


